import { createContext, Dispatch, PropsWithChildren, SetStateAction, useEffect, useState } from 'react';
import { Card, Form, ListGroup, Button, Modal, Alert } from 'react-bootstrap';
import { ChevronDown, ChevronUp, X } from "react-bootstrap-icons";
import { addLineToCart, createCart, createCheckout, payCheckout } from './product-manager.service';
import { AttributeGroup, Package, PackageAttribute, Product, ProductAttribute } from './types';
import { JSONPath } from 'jsonpath-plus';
import { LoadingActionPendingModal } from './LoadingActionPendingModal';
import fr from './translations/fr.json'
import en from './translations/en.json'
import humanFormat from 'human-format';
import { UsageQuotas } from './UageQuotas';
import './css/main.css'



export const ProductManagerSelectedItemsContext = createContext<{
    selectedItems: ((Package | Product) & { selectedProductAttributesIds?: number[], selectedPackageAttributesIds?: number[] })[],
    setSelectedItems: Dispatch<SetStateAction<((Package | Product) & { selectedProductAttributesIds?: number[], selectedPackageAttributesIds?: number[] })[]>>
}>({
    selectedItems: [],
    setSelectedItems: () => { }
})


function t(key: string, currentLanguage = "fr") {
    return JSONPath({ path: key, json: currentLanguage === 'fr' ? fr : currentLanguage === 'en' ? en : fr });
}

function getAttributeById(id: number, entity: ((Package | Product) & { selectedProductAttributesIds?: number[], selectedPackageAttributesIds?: number[] })): (PackageAttribute | ProductAttribute | null) {
    const allAttributes = (entity.attributeGroups as AttributeGroup<PackageAttribute | ProductAttribute>[]).reduce((array: (PackageAttribute | ProductAttribute)[], attributeGroup: AttributeGroup<PackageAttribute | ProductAttribute>) => {
        const _attributes = attributeGroup.attributes.reduce((_array: (PackageAttribute | ProductAttribute)[], attribute: PackageAttribute | ProductAttribute) => {
            _array = [..._array, attribute]
            return _array
        }, [] as (PackageAttribute | ProductAttribute)[])
        array = [...array, ..._attributes]
        return array
    }, [] as (PackageAttribute | ProductAttribute)[])
    return allAttributes.find((attribute: PackageAttribute | ProductAttribute) => attribute.id === id) || null
}

function checkMaxProductUsageInSelection(selectedItems: (Product | Package)[], currentLanguage: 'en' | 'fr'): { error: string, productId: number }[] {
    let _packages: Package[] = [];
    let products: Product[] = [];

    _packages = selectedItems.filter((item) => item.isAvailable && (item as Package).productPackages !== undefined) as Package[]
    products = selectedItems.filter((item) => item.isAvailable && (item as Package).productPackages === undefined) as Product[]
    const allProducts = [
        ...products,
        ...(_packages.reduce(
            (array, _package) => {
                const productPackageProducts = _package.productPackages.map(({ product }) => product)
                array = [...array, ...productPackageProducts]
                return array
            }
            ,
            [] as Product[]))
    ]
    const checks = allProducts.reduce((map, product) => {
        if (!map[product.id]) {
            map[product.id] = { productId: product.id, nextUsage: product.currentUsage + 1, maxUsage: product.maxUsage, isValid: product.maxUsage === 0 ? true : product.currentUsage + 1 <= product.maxUsage, displayLabel: product.displayLabel || "" }
        } else {
            map[product.id] = { productId: product.id, nextUsage: map[product.id].nextUsage + 1, maxUsage: product.maxUsage, isValid: product.maxUsage === 0 ? true : map[product.id].nextUsage + 1 <= product.maxUsage, displayLabel: product.displayLabel || "" }
        }
        return map;
    }, {} as Record<number, { nextUsage: number, maxUsage: number, isValid: boolean, displayLabel: string, productId: number }>)

    return Object.values(checks).filter(({ isValid }) => !isValid).map(({ displayLabel, productId }) => ({ error: `${t(displayLabel, currentLanguage)}: ${t('productOveruseError', currentLanguage)}`, productId }))
}


function calculateTotalPrice(selectedItems: ((Product | Package) & { selectedProductAttributesIds?: number[], selectedPackageAttributesIds?: number[] })[]) {
    return selectedItems.reduce((sum, item) => {
        const increasePriceValue = (item.attributeGroups as AttributeGroup<PackageAttribute | ProductAttribute>[]).reduce((attributeGroupSum: number, attributeGroup: AttributeGroup<PackageAttribute | ProductAttribute>) => {
            attributeGroupSum += attributeGroup.attributes.reduce((attributeSum: number, attribute: PackageAttribute | ProductAttribute) => {
                if (item.selectedProductAttributesIds?.includes(attribute.id as number)) {
                    attributeSum += attribute.increasePriceValue
                }
                if (item.selectedPackageAttributesIds?.includes(attribute.id as number)) {
                    attributeSum += attribute.increasePriceValue
                }
                return attributeSum
            }, 0)
            return attributeGroupSum
        }, 0) || 0
        sum += (item?.prices?.[0]?.value || 0 + increasePriceValue);
        return sum
    }, 0)
}

export interface SwitchButtonProps {
    label: string
}

export function SwitchButton({
    label = "Check this"
}: SwitchButtonProps) {
    return (
        <Form>
            <Form.Check
                type="switch"
                label={label}
            />
        </Form>
    );
}


export interface PriceDisplayProps { creditSystemEnabled: boolean, value: number | string, currentLanguage: string }

export function PriceDisplay({
    creditSystemEnabled,
    value,
    currentLanguage
}: PriceDisplayProps) {
    return creditSystemEnabled ? < span className="py-2 px-4 bg-pink rounded-pill text-white fs-6" > {value} {t("credits", currentLanguage)}</span > : null
}

export interface PackageCard {
    _package: Package | null
    currentLanguage: 'fr' | 'en',
    isSelected: boolean
    onSelect: () => void
    selectedItems?: ((Package | Product) & { selectedProductAttributesIds?: number[], selectedPackageAttributesIds?: number[] })[],
    creditSystemEnabled: boolean
}
export function PackageCard({
    _package = null,
    currentLanguage = 'fr',
    isSelected = false,
    onSelect = () => { return; },
    selectedItems = [],
    creditSystemEnabled = false
}: PackageCard) {
    return _package ?
        <div className='d-flex flex-column gap-2'>
            <Card className={`cursor-pointer rounded ${isSelected ? 'border border-2' : ''}  ${checkMaxProductUsageInSelection(selectedItems, currentLanguage)
                .filter((error) => _package.productPackages.find((productPackage) => productPackage.product.id === error.productId)).length ? `border-danger` : `border-primary`} `} style={{ width: '100%' }} onClick={onSelect}>
                <Card.Body>
                    <div className="d-flex justify-content-between">
                        <div className="d-flex w-75 flex-column">
                            <Card.Title className='fs-4'>{t(_package?.displayLabel || "", currentLanguage)}</Card.Title>
                            <Card.Text className='fs-8'>
                                {t(_package?.displayDescription || "", currentLanguage)}
                            </Card.Text>
                        </div>
                        <div className='d-flex w-25 justify-content-end' style={{ gap: '1rem' }}>
                            {_package.prices?.length && _package.prices[0].value !== undefined && creditSystemEnabled ?
                                <span className='text-pink'>
                                    {_package.prices[0].value || 0}  {t("credits", currentLanguage)}
                                </span>
                                : null}
                        </div>
                    </div>
                </Card.Body>
            </Card>

            {
                checkMaxProductUsageInSelection(selectedItems, currentLanguage)
                    .filter((error) => _package.productPackages.find((productPackage) => productPackage.product.id === error.productId)).map((error, index) => {
                        return <p key={`package-card-error-product-package-${error.productId}`} className='text-danger'>
                            {error.error}
                        </p>
                    })
            }
        </div> : null
}

export interface ProductCardProps {
    product: Product | null,
    currentLanguage: 'fr' | 'en'
    isSelected: boolean
    onSelect: () => void
    errors?: any[]
    creditSystemEnabled: boolean
}
export function ProductCard({
    product = null,
    currentLanguage = "fr",
    isSelected = false,
    onSelect = () => { return },
    errors = [],
    creditSystemEnabled = false
}: ProductCardProps) {
    return product ? <Card
        className={`cursor-pointer rounded ${isSelected ? 'border border-2' : ''} ${errors.length ? `border-danger` : `border-primary`}`} style={{ width: '100%' }}
        onClick={onSelect}>
        <Card.Body>
            <div className="d-flex justify-content-between">
                <div className="d-flex w-75 flex-column">
                    <Card.Title className='fs-4'>{t(product?.displayLabel || "", currentLanguage)}</Card.Title>
                    <Card.Text className='fs-8'>
                        {t(product?.displayDescription || "", currentLanguage)}
                    </Card.Text>
                </div>
                <div className='d-flex w-25 justify-content-end' style={{ gap: '1rem' }}>
                    {product.prices?.length && product.prices[0].value !== undefined && creditSystemEnabled ?
                        <span>
                            {product.prices[0].value || 0}  {t("credits", currentLanguage)}
                        </span> : null}
                </div>
            </div>

        </Card.Body>
    </Card> : null
}


export interface RecapCardProps {
    selectedItems: ((Package | Product) & { selectedProductAttributesIds?: number[], selectedPackageAttributesIds?: number[] })[],
    currentLanguage: 'fr' | 'en'
    userBalance: number | null;
    creditSystemEnabled: boolean
}
export function RecapCard({
    currentLanguage = "fr",
    selectedItems = [],
    userBalance = null,
    creditSystemEnabled = false
}: RecapCardProps) {

    return <div className="d-flex flex-column" style={{ width: "100%", ...(!selectedItems?.length ? { display: 'flex', justifyContent: 'center' } : {}) }}>
        <h5 className='fs-6 text-center'>{!selectedItems.length ? t('noActiveSelection', currentLanguage) : null}</h5>

        {selectedItems?.length ? <hr style={{ marginBottom: '1rem' }} /> : null}

        {selectedItems.length ? <ListGroup style={{ paddingLeft: 0 }} className="list-group-flush">

            {selectedItems.map((item) =>
                <ListGroup.Item key={`recap-card-item-${item.id}`} className='d-flex flex-column' style={{ width: '100%' }}>
                    <div className="d-flex justify-content-between">
                        <Card.Title className='fs-6 fw-bold'>{t(item?.displayLabel || "", currentLanguage)}</Card.Title>
                        <div className="d-flex align-items-center" style={{ gap: '1rem' }}>
                            {item.prices?.length && item.prices[0].value && creditSystemEnabled ?
                                <span className='fs-6'>
                                    {item.prices[0].value || 0}  {t("credits", currentLanguage)}
                                </span> :
                                null}

                            {!item?.prices?.[0].value && creditSystemEnabled ?
                                <span className='fs-6'>
                                    {t("free", currentLanguage)}
                                </span> :
                                null}
                        </div>
                    </div>
                    {item.selectedProductAttributesIds?.map((id) =>
                        <div key={`selected-attribute-${id}`} className='d-flex justify-content-between align-items-center'>
                            <Card.Title className='fs-6 fw-bold'>{`${getAttributeById(id, item)?.value || ""} ${t(getAttributeById(id, item)?.displayLabel || "", currentLanguage)}`}</Card.Title>
                            {getAttributeById(id, item)?.increasePriceValue ? <span className='fs-6'>
                                {getAttributeById(id, item)?.increasePriceValue}  {t("credits", currentLanguage)}
                            </span> : null}
                        </div>

                    )}
                </ListGroup.Item>
            )}

        </ListGroup> : null}

        {selectedItems?.length ? <hr style={{ marginBottom: '1rem' }} /> : null}

        {creditSystemEnabled ? <div className="d-flex align-items-center justify-content-end">
            <span className='d-flex align-items-center fs-4 fw-bold gap-4'>
                {t("greatTotal", currentLanguage)}
                <span className='py-2 px-4 bg-pink rounded-pill text-white fs-8'>
                    {calculateTotalPrice(selectedItems) ? `${calculateTotalPrice(selectedItems)} ${t("credits", currentLanguage)} ` : t("free", currentLanguage)}
                </span>
            </span>
        </div> : null}

        <div className="d-flex align-items-center justify-content-end mt-4">
            {selectedItems.length && userBalance !== null && creditSystemEnabled ? <span className={`${(userBalance === 0 || calculateTotalPrice(selectedItems) > userBalance) ? 'text-danger' : ''}`}>
                {`${t("userBalance", currentLanguage)} `}
                <span>{`${humanFormat(userBalance)} ${t("credits", currentLanguage)}`}</span>
            </span> : null}
        </div>

    </div>

}


export interface WarningSelectionCardProps {
    selectedItems: ((Package | Product) & { selectedProductAttributesIds?: number[], selectedPackageAttributesIds?: number[] })[],
    currentLanguage: 'fr' | 'en',
    userBalance: number | null
}
export function WarningSelectionCard({
    currentLanguage = "fr",
    selectedItems = [],
    userBalance = null
}: WarningSelectionCardProps) {

    return <div className='w-100'>
        <ListGroup style={{ padding: 0 }} className="list-group-flush my-4">
            {userBalance !== null ?
                (userBalance === 0 || calculateTotalPrice(selectedItems) > userBalance) ?
                    <ListGroup.Item
                        className='d-flex justify-content-between'
                        style={{ width: '100%', paddingLeft: 0, }}>

                        <Card.Title className='fs-8 fw-light text-danger'>
                            <X color="currentColor" size={20} />
                            {t('insufficientBalanceError')}
                        </Card.Title>
                    </ListGroup.Item>
                    : null
                : null}
        </ListGroup>
    </div>

}


export interface WarningQuotasProps {
    currentLanguage: string;
    products: Product[]
}
export function WarningQuotas({
    currentLanguage,
    products = []
}: WarningQuotasProps) {
    const [showQuotas, setShowQuotas] = useState<boolean>(false)
    return <>
        <ToggleContentContainer label={null}>
            <Alert variant={'warning'}>
                {t('actionSelection.noAvailableAction', currentLanguage)}
                <Alert.Link href="#" onClick={() => setShowQuotas(true)}> {t('actionSelection.goToQuota', currentLanguage)}</Alert.Link>.
            </Alert>
        </ToggleContentContainer>


        <Modal
            fullscreen="md-down"
            size='xl'
            aria-labelledby="contained-modal-title-vcenter"
            show={showQuotas}
            onHide={() => setShowQuotas(false)}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title className='fs-4 fw-bold'>
                    {t(
                        "modalQuotas.title", currentLanguage
                    )}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className='d-flex flex-column align-items-center justify-content-start gap-4'>
                <Alert variant='warning'>
                    {t("modalQuotas.text", currentLanguage)}
                </Alert>
                <UsageQuotas products={products as Product[]} currentLanguage={currentLanguage} />
            </Modal.Body>
        </Modal>
    </>
}

export interface ToggleContentContainerProps extends PropsWithChildren {
    label: string | null,
    initialToogled?: boolean
    isToggling?: boolean
}

export function ToggleContentContainer({
    children,
    label,
    initialToogled = false,
    isToggling = false
}: ToggleContentContainerProps) {

    const [toggled, setToggled] = useState<boolean>(initialToogled)
    return <div className='d-flex flex-column' style={{ gap: "1rem", width: '100%', height: '100%' }}>
        <div className="d-flex align-items-center justify-content-between">
            {label ? <p className='fs-4 mb-0'>
                {label}
            </p> : null}
            {isToggling ? <div className="d-flex align-items-center" style={{
                gap: '1rem'
            }}>
                <span className='cursor-pointer' onClick={() => setToggled((state) => !state)}>
                    {toggled ? <ChevronUp color="currentColor" size={20} /> : <ChevronDown color="currentColor" size={20} />}
                </span>
            </div> : null}
        </div>

        {isToggling ? toggled ? children : null : children}
    </div>

}


export interface ModalConfirmProps {
    creditSystemEnabled: boolean;
    currentLanguage?: 'fr' | 'en'
    userBalance: number | null;
    getCreateDiagMetadata?: () => Record<string, any>,
    getActionMetadata?: () => Record<string, any>
    diagUUID?: string | null;
    selectedItems: ((Package | Product) & { selectedProductAttributesIds?: number[], selectedPackageAttributesIds?: number[] })[];
    loadingImageBaneerSrc: string
    setIsActionPending: (params: { checkoutId: number, pendingActionCount: number }) => void;
    isActionPending: boolean
    show: boolean;
    createNewDiagnosis?: (params: Record<string, any>) => Promise<string> | null
    setShow: Dispatch<SetStateAction<boolean>>
}


export function ModalConfirm({
    creditSystemEnabled = false,
    currentLanguage = 'fr',
    userBalance = null,
    createNewDiagnosis = async (params: Record<string, any>) => "",
    getCreateDiagMetadata = () => { return ({}) },
    getActionMetadata = () => { return ({}) },
    diagUUID = null,
    selectedItems = [],
    loadingImageBaneerSrc = "",
    show = false,
    setShow = () => { return },
    isActionPending = false,
    setIsActionPending = () => { return; },
}: ModalConfirmProps) {

    async function handleConfirm() {
        let _diagUUID = diagUUID
        if (!_diagUUID) {
            _diagUUID = await createNewDiagnosis({
                ...getCreateDiagMetadata()
            })
        }
        const _packages = selectedItems.filter((item) => item.hasOwnProperty('productPackages')) as (Package & { selectedPackageAttributesIds?: number[] })[]
        const products = selectedItems.filter((item) => !item.hasOwnProperty('productPackages')) as (Product & { selectedProductAttributesIds?: number[] })[]
        const actions: string[] = [
            ..._packages.reduce((productsInPackage, _package) => {
                productsInPackage = [
                    ...productsInPackage,
                    ..._package.productPackages.map((productPackage) => productPackage.product)
                ]
                return productsInPackage
            }, [] as Product[])
                .map((product) => product.action),
            ...products.map((product) => product.action)
        ]
        try {
            const cart: { id: number } = await createCart()
            for (const _package of _packages) {
                await addLineToCart(cart.id, {
                    isPackage: true,
                    packageId: _package.id,
                    selectedPackageAttributesIds: _package.selectedPackageAttributesIds,
                    quantity: 1,
                    metadata: {
                        ...getActionMetadata(),
                        diagUUID: _diagUUID as string
                    }
                })
            }

            for (const product of products) {
                await addLineToCart(cart.id, {
                    isPackage: false,
                    productId: product.id,
                    quantity: 1,
                    selectedProductAttributesIds: product.selectedProductAttributesIds,
                    metadata: {
                        ...getActionMetadata(),
                        diagUUID: _diagUUID as string
                    }
                })
            }
            const checkout: { id: number } = await createCheckout(cart.id)
            await payCheckout(checkout.id)
            setIsActionPending({ checkoutId: checkout.id, pendingActionCount: actions.length })
        } catch (error) {
            console.error(error)
        }
    }

    return (
        <>
            <Modal fullscreen="md-down"
                aria-labelledby="contained-modal-title-vcenter"
                show={show}
                onHide={() => setShow(false)}
                centered>
                <Modal.Header closeButton>
                    <Modal.Title className='fs-4 fw-bold'>
                        {t(
                            "modalConfirm.title", currentLanguage
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className='d-flex flex-column align-items-center justify-content-center'>

                    <div className="d-flex flex-column gap-4">
                        <h1 className='fs-1 text-center'>
                            {t("modalConfirm.header", currentLanguage)}
                        </h1>

                        <h2 className="fs-6 fw-normal">
                            {t("modalConfirm.subHeader", currentLanguage)}
                        </h2>
                    </div>

                    <div className='overflow-auto pb-10' style={{
                        width: '100%'
                    }}>

                        <ToggleContentContainer label={null}>
                            <div className='d-flex flex-column' style={{ gap: "1rem" }} >
                                <RecapCard creditSystemEnabled={creditSystemEnabled} currentLanguage={currentLanguage} selectedItems={selectedItems} userBalance={userBalance} />
                            </div>
                        </ToggleContentContainer>

                        {userBalance !== null && calculateTotalPrice(selectedItems) > userBalance ? <hr /> : null}

                        {userBalance !== null && calculateTotalPrice(selectedItems) > userBalance ?
                            <ToggleContentContainer initialToogled={true} label={t("warningHeader", currentLanguage)}>
                                <div className='d-flex flex-column' style={{ gap: "1rem" }} >
                                    <WarningSelectionCard userBalance={userBalance} currentLanguage={currentLanguage} selectedItems={selectedItems} />
                                </div>
                            </ToggleContentContainer>
                            : null}

                    </div>
                </Modal.Body>
                <Modal.Footer className='d-flex justify-content-center'>
                    <div className="d-flex w-100 justify-content-between">
                        <Button
                            variant="secondary"
                            onClick={() => setShow(false)}
                        >
                            {t("modalConfirm.actions.cancel.label", currentLanguage)}
                        </Button>
                        <Button disabled={
                            !selectedItems.length || checkMaxProductUsageInSelection(selectedItems, currentLanguage).length || (userBalance !== null && calculateTotalPrice(selectedItems) > userBalance) ? true : false
                        }
                            size='lg'
                            variant="success"
                            onClick={handleConfirm}
                        >
                            {t("modalConfirm.actions.confirm.label", currentLanguage)}
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>

            {isActionPending ?
                <LoadingActionPendingModal show={true} close={() => { }} imageBaneerSrc={loadingImageBaneerSrc} />
                : null
            }

        </>
    );
}



export interface ProductManagerProps extends PropsWithChildren {
    productCategorySlug: string,
    currentLanguage?: 'fr' | 'en'
    actionLabel?: string
    userBalance: number | null;
    createNewDiagnosis?: (params: Record<string, any>) => Promise<string> | null
    getCreateDiagMetadata?: () => Record<string, any>,
    getActionMetadata?: () => Record<string, any>
    actionDisabled?: boolean;
    diagUUID?: string | null;
    lgColumnHeight?: string | number | undefined
    loadingImageBaneerSrc: string
    setIsActionPending: (params: { checkoutId: number, pendingActionCount: number }) => void;
    creditSystemEnabled: boolean;
    isActionPending: boolean
}